import {useState} from 'react'
import isEqual from 'lodash/isEqual.js'
import isNil from 'lodash/isNil.js'
import pick from 'lodash/pick.js'

import OmniBar, {parseInputValue} from '../../common/components/List/OmniBar.js'
import {
  API_PRODUCT_ACTIVE,
  PRODUCT_FULFILLMENT_CHANNELS,
} from '../../common/constants/Products.js'
import {
  TAG_FILTER_OR,
  TAG_FILTER_AND,
  TAG_FILTER_ONLY,
  TAG_FILTER_NOT,
} from '../../common/constants/Tags.js'

import {activeCartsSortedByNameSelector} from '../../data/carts.js'
import {
  getSupplierName,
  suppliersSortedByNameSelector,
} from '../../data/suppliers.js'
import {fulfillmentChannelsSelector} from '../../data/products.js'
import {productTagsSortedByNameSelector} from '../../data/productTags.js'
import {
  setFulfillmentChannelFilters,
  setCategory,
  setProductFilters,
  setSearchText,
  updateProductTagFilters,
  updateProductUntagged,
  setProductTagFilterBy,
  stocktakeDetailQuerySelector,
  navigateStocktakeDetail,
  setProductSupplierIDs,
  removeProductSupplierID,
  removeProductCartID,
  setProductUntagged,
  removeProductTagFilter,
  navigateToStocktakeDetailPage,
  updateProductFilters,
  setProductCartIDs,
  updateProductExcludeTags,
  removeProductExcludeTag,
} from './stocktakeDetailFunctions.js'
import {getState, useSelector} from '../../store.js'
import {
  STOCKTAKE_LINE_TROOLEAN_FILTER_OPTION_TOKENS,
  STOCKTAKE_LINE_TROOLEAN_FILTER_OPTIONS,
  STOCKTAKE_LINE_TROOLEAN_FILTERS,
} from '../../common/constants/Stocktakes.js'
import {filterOptions} from '../../common/tokenizeOptions.js'

export const SEARCH_TYPE = 'search'
export const CART_TYPE = 'cart'
export const SUPPLIER_TYPE = 'supplier'
export const FULFILLMENT_CHANNEL_TYPE = 'fulfillment_channel'
export const CATEGORY_TYPE = 'category'
export const FILTERS_TYPE = 'filters'
export const QUANTITY_TYPE = 'quantity'
export const TAG_TYPE = 'tag'
export const TAG_AND_TYPE = 'tag_and'
export const TAG_ONLY_TYPE = 'tag_only'
export const TAG_NOT_TYPE = 'tag_not'
const TAG_TYPES = {
  [TAG_FILTER_OR]: TAG_TYPE,
  [TAG_FILTER_AND]: TAG_AND_TYPE,
  [TAG_FILTER_ONLY]: TAG_ONLY_TYPE,
  [TAG_FILTER_NOT]: TAG_NOT_TYPE,
}
export const UNTAGGED_TYPE = 'untagged'
export const EXCLUDE_TAGS_TYPE = 'exclude_tags'

export function stocktakeDetailOmniBarFilterListSelector(state) {
  const cache = stocktakeDetailOmniBarFilterListSelector.cache
  const carts = activeCartsSortedByNameSelector(state)
  const suppliers = suppliersSortedByNameSelector(state)
  const fulfillmentChannels = fulfillmentChannelsSelector(state)
  const productTags = productTagsSortedByNameSelector(state)
  const query = stocktakeDetailQuerySelector(state)

  if (
    cache &&
    isEqual(carts, cache.carts) &&
    isEqual(suppliers, cache.suppliers) &&
    isEqual(fulfillmentChannels, cache.fulfillmentChannels) &&
    isEqual(productTags, cache.productTags) &&
    query === cache.query
  ) {
    return cache.list
  }

  stocktakeDetailOmniBarFilterListSelector.cache = {
    carts,
    suppliers,
    fulfillmentChannels,
    productTags,
    query,
  }

  const list = stocktakeDetailFilterList({
    carts,
    suppliers,
    fulfillmentChannels,
    productTags,
    query,
  })

  stocktakeDetailOmniBarFilterListSelector.cache.list = list

  return list
}

export function stocktakeDetailFilterList({
  carts,
  suppliers,
  fulfillmentChannels,
  productTags,
  query: {
    supplier = [],
    sales_channel = [],
    category,
    tag = [],
    tag_filter_by,
    exclude_tags = [],
    untagged,
    search,
    ...query
  },
}) {
  const list = []

  const filters = pick(query, STOCKTAKE_LINE_TROOLEAN_FILTERS)
  const fulfillmentChannelFilters = pick(query, PRODUCT_FULFILLMENT_CHANNELS)

  STOCKTAKE_LINE_TROOLEAN_FILTER_OPTIONS.forEach((filter) => {
    if (isNil(filters[filter.value])) {
      return
    }

    list.push({
      type: FILTERS_TYPE,
      label: filters[filter.value] ? filter.display : filter.reverseDisplay,
      value: filter.value,
    })
  })

  suppliers.forEach((s) => {
    if (!supplier.includes(s.id)) {
      return
    }

    list.push({
      type: SUPPLIER_TYPE,
      label: getSupplierName(s),
      value: `${SUPPLIER_TYPE}:${s.id}`,
      id: s.id,
    })
  })

  fulfillmentChannels.forEach((option) => {
    if (!fulfillmentChannelFilters[option.value]) {
      return
    }

    list.push({
      type: FULFILLMENT_CHANNEL_TYPE,
      label: option.display,
      value: option.value,
    })
  })

  carts.forEach((cart) => {
    if (!sales_channel.includes(cart.id)) {
      return
    }

    list.push({
      type: CART_TYPE,
      label: cart.name,
      value: `${CART_TYPE}:${cart.id}`,
      id: cart.id,
    })
  })

  if (category) {
    list.push({
      type: CATEGORY_TYPE,
      label: category,
      value: `${CATEGORY_TYPE}:${category}`,
    })
  }

  const tagType = TAG_TYPES[tag_filter_by || TAG_FILTER_OR]
  for (const t of productTags) {
    if (tag.includes(t.name)) {
      list.push({
        type: tagType,
        label: t.name,
        value: `${tagType}:${t.id}`,
        color: t.color,
        id: t.id,
      })
    }
  }

  for (const t of productTags) {
    if (exclude_tags.includes(t.name)) {
      list.push({
        type: EXCLUDE_TAGS_TYPE,
        label: t.name,
        value: `${EXCLUDE_TAGS_TYPE}:${t.id}`,
        color: t.color,
        name: t.name,
      })
    }
  }

  if (untagged) {
    list.push({
      type: UNTAGGED_TYPE,
      label: 'Untagged',
      value: 'untagged',
    })
  }

  if (search) {
    list.push({
      type: SEARCH_TYPE,
      label: search,
      value: search,
    })
  }

  return list
}

export const PROCESS_VALUE_FUNCTIONS = {
  processStatusFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}

    if (
      !(filterType.match(/^status$/i) && ['', '-'].includes(filterOperator))
    ) {
      return outcome
    }

    outcome.stop = true

    let reverse = filterOperator === '-'

    const options = filterOptions(
      filterValue,
      STOCKTAKE_LINE_TROOLEAN_FILTER_OPTION_TOKENS,
    )

    if (options.length) {
      outcome.hash = setProductFilters({
        [options[0].value]: reverse ? false : true,
      })
    }

    return outcome
  },

  processCategoryFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}

    if (!filterType.match(/^cat(egory)?$/i)) {
      return outcome
    }

    outcome.stop = true

    outcome.hash = setCategory(filterValue)

    return outcome
  },

  processSupplierFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^supplier[s]?$/i) &&
        ['', '='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const suppliers = suppliersSortedByNameSelector(getState())

    const matchedSuppliers = suppliers.filter((s) => {
      if (filterOperator === '=') {
        if (s.id === Number(filterValue)) {
          return true
        }
        if (s.address.company === filterValue) {
          return true
        }
      } else {
        if (
          `${s.address.company} ${s.address.city} ${s.address.state}`.match(
            RegExp(filterValue, 'i'),
          )
        ) {
          return true
        }
      }

      return false
    })

    if (matchedSuppliers.length) {
      outcome.hash = setProductSupplierIDs([
        ...stocktakeDetailQuerySelector(getState()).supplier,
        ...matchedSuppliers.map(({id}) => id),
      ])
    }

    return outcome
  },

  processFulfillmentChannelFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^(fulfillment|fulfillment channel|channel|fc)$/i) &&
        ['', '='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const fulfillmentChannels = fulfillmentChannelsSelector(getState())

    const matchedFulfillmentChannel = fulfillmentChannels.find((fc) => {
      if (fc.display.match(RegExp(filterValue, 'i'))) {
        return true
      }

      return false
    })

    if (matchedFulfillmentChannel) {
      outcome.hash = setFulfillmentChannelFilters({
        [matchedFulfillmentChannel.value]: true,
      })
    }

    return outcome
  },

  processCartFilterValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^(cart|sales channel)[s]?$/i) &&
        ['', '='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const carts = activeCartsSortedByNameSelector(getState())

    const matchedCarts = carts.filter((c) => {
      if (filterOperator === '=') {
        if (c.id === Number(filterValue)) {
          return true
        }
        if (c.name === filterValue) {
          return true
        }
      } else {
        if (`${c.name} ${c.vendor}`.match(RegExp(filterValue, 'i'))) {
          return true
        }
      }

      return false
    })

    if (matchedCarts.length) {
      outcome.hash = setProductCartIDs([
        ...stocktakeDetailQuerySelector(getState()).sales_channel,
        ...matchedCarts.map(({id}) => id),
      ])
    }

    return outcome
  },

  processTagFilterValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^(tag)[s]?$/i) &&
        ['', '=', '-', '!', '!='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const productTags = productTagsSortedByNameSelector(getState())
    const query = stocktakeDetailQuerySelector(getState())

    const matchedTags = productTags.filter((t) => {
      if (['=', '!='].includes(filterOperator)) {
        if (t.id === Number(filterValue)) {
          return true
        }
        if (t.name === filterValue) {
          return true
        }
      } else {
        if (`${t.name}`.match(RegExp(filterValue, 'i'))) {
          return true
        }
      }

      return false
    })

    const negate = ['!', '-', '!='].includes(filterOperator)

    if (matchedTags.length) {
      let updates = updateProductUntagged(false)

      if (negate) {
        updates = {
          ...updates,
          ...updateProductExcludeTags([
            ...query.exclude_tags,
            ...matchedTags.map(({name}) => name),
          ]),
        }
      } else {
        updates = {
          ...updates,
          ...updateProductTagFilters([
            ...query.tag,
            ...matchedTags.map(({name}) => name),
          ]),
        }
      }
      outcome.hash = navigateStocktakeDetail(updates)
    }

    return outcome
  },

  processUntaggedValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (!filterValue.match(/^untag(ged)?$/i)) {
      return outcome
    }

    outcome.stop = true

    outcome.hash = navigateStocktakeDetail({
      ...updateProductUntagged(true),
      ...updateProductTagFilters([]),
    })

    return outcome
  },

  processTagFilterByValue(inputValue, filterType, filterOperator, filterValue) {
    const outcome = {stop: false}

    if (
      !(
        filterType.match(/^(tag.filter(ing)?.logic|tfl)$/i) &&
        ['', '='].includes(filterOperator)
      )
    ) {
      return outcome
    }

    outcome.stop = true

    const filterBy = filterValue.match(/^(and|all)$/i)
      ? TAG_FILTER_AND
      : filterValue.match(/^only$/i)
        ? TAG_FILTER_ONLY
        : filterValue.match(/^not$/i)
          ? TAG_FILTER_NOT
          : TAG_FILTER_OR

    outcome.hash = setProductTagFilterBy(filterBy)

    return outcome
  },

  // Put new functions before this function
  processSearchTextFilterValue(inputValue) {
    return {stop: true, hash: setSearchText(inputValue)}
  },
  // Do not put functions after this function
}

export function processInputValue(inputValue, setInputValue) {
  if (!inputValue) {
    setInputValue('')

    return
  }

  const [, filterType, filterOperator, filterValue] =
    parseInputValue(inputValue)

  let hash
  for (const processFilterValue of Object.values(PROCESS_VALUE_FUNCTIONS)) {
    const outcome = processFilterValue(
      inputValue,
      filterType,
      filterOperator,
      filterValue,
    )

    if (outcome.stop) {
      hash = outcome.hash
      break
    }
  }

  if (hash) {
    setInputValue('')

    return hash
  }

  return
}

export function removeValue(value) {
  if (value.type === CART_TYPE) {
    return removeProductCartID(value.id)
  } else if (value.type === SUPPLIER_TYPE) {
    return removeProductSupplierID(value.id)
  } else if (value.type === FULFILLMENT_CHANNEL_TYPE) {
    return setFulfillmentChannelFilters({[value.value]: null})
  } else if (value.type === CATEGORY_TYPE) {
    return setCategory('')
  } else if (value.type === FILTERS_TYPE) {
    return setProductFilters({[value.value]: null})
  } else if (
    [TAG_TYPE, TAG_AND_TYPE, TAG_ONLY_TYPE, TAG_NOT_TYPE].includes(value.type)
  ) {
    return removeProductTagFilter(value.label)
  } else if (value.type === UNTAGGED_TYPE) {
    return setProductUntagged(false)
  } else if (value.type === EXCLUDE_TAGS_TYPE) {
    return removeProductExcludeTag(value.label)
  } else if (value.type === SEARCH_TYPE) {
    return setSearchText('')
  }
}

export function clearAll() {
  return navigateToStocktakeDetailPage(
    updateProductFilters({
      [API_PRODUCT_ACTIVE]: null,
    }),
  )
}

export function editValue(data, setInputValue) {
  if (data.type === SEARCH_TYPE) {
    setInputValue(data.value)
  }
}

export default function StocktakeDetailOmniBar() {
  const [inputValue, setInputValue] = useState('')
  const list = useSelector(stocktakeDetailOmniBarFilterListSelector)

  return (
    <OmniBar
      placeholder="Enter a product name or SKU..."
      inputValue={inputValue}
      value={list}
      onRemoveValue={(removedValue) => removeValue(removedValue)}
      onClear={() => clearAll()}
      onInputValueChange={(inputValue) => setInputValue(inputValue)}
      onProcessInputValue={(inputValue) =>
        processInputValue(inputValue, setInputValue)
      }
      onEditValue={(data) => editValue(data, setInputValue)}
    />
  )
}
